import { z } from "zod";
import { safeFetch } from "../http/http";
import { requestor } from "../requestor";

const baseUrl = "https://vistacart.orders.vpsvc.com";

const productAttributeSchema = z.object({
  key: z.string(),
  value: z.string(),
});

const cartLineItemSchema = z.object({
  customOrderData: z.object({
    analytics: z
      .object({
        product_id: z.string().optional(),
        workId: z.string().optional(),
        albelliDocId: z.string().optional(),
        logoId: z.string().optional(),
      })
      .optional(),
  }),
  designData: z
    .object({
      documentReferenceUrl: z.string().url().optional(),
      livePreviewUrl: z.string().url().optional(),
    })
    .optional(),
  productData: z.object({
    productKey: z.string(),
    productVersion: z.number(),
    selectedAttributes: z.array(productAttributeSchema),
  }),
  merchandisingData: z.object({
    productName: z.string().optional(),
  }),
  quantity: z.number().optional(),
});

const cartItemsResponseSchema = z.object({
  lineItems: z.array(cartLineItemSchema),
});

export type ProductAttribute = z.infer<typeof productAttributeSchema>;

export type CartLineItem = z.infer<typeof cartLineItemSchema>;

export async function fetchCartItems(
  tenant: string,
  shopperId: string,
  authorizationHeader: string,
): Promise<CartLineItem[]> {
  const response = await safeFetch(
    cartItemsResponseSchema,
    `${baseUrl}/tenants/${tenant}/carts/${shopperId}?requestor=${requestor}`,
    {
      headers: { Authorization: authorizationHeader },
    },
  );

  return response.lineItems;
}

const cartTenantMappings: Record<string, string> = {
  "da-dk": "VP-DK-PROD",
  "de-at": "VP-AT-PROD",
  "de-ch": "VP-CH-PROD",
  "de-de": "VP-DE-PROD",
  "en-au": "VP-AU-PROD",
  "en-ca": "VP-CA-PROD",
  "en-gb": "VP-GB-PROD",
  "en-ie": "VP-IE-PROD",
  "en-in": "VP-IN-PROD",
  "en-nz": "VP-NZ-PROD",
  "en-sg": "VP-SG-PROD",
  "en-us": "VP-US-PROD",
  "es-es": "VP-ES-PROD",
  "es-us": "VP-US-PROD",
  "fi-fi": "VP-FI-PROD",
  "fr-ca": "VP-CA-PROD",
  "fr-be": "VP-BE-PROD",
  "fr-ch": "VP-CH-PROD",
  "fr-fr": "VP-FR-PROD",
  "it-ch": "VP-CH-PROD",
  "it-it": "VP-IT-PROD",
  "jp-jp": "VP-JP-PROD",
  "nb-no": "VP-NO-PROD",
  "nl-be": "VP-BE-PROD",
  "nl-nl": "VP-NL-PROD",
  "pt-pt": "VP-PT-PROD",
  "sv-se": "VP-SE-PROD",
};

export function getCartTenant(locale: string) {
  const tenant = cartTenantMappings[locale.toLowerCase()];
  if (!tenant) {
    throw new Error(`No matching cart tenant for locale ${locale}`);
  }
  return tenant;
}
